import { useCallback, useEffect, useRef, useState } from "react"

import { useToast } from "@/components/ui"
import { IS_DEV } from "@/lib/constants"

import { useGetVersionQuery as useGetVersionQueryAdmin } from "../graphql/admin-generated-rq"
import { useGetVersionQuery as useGetVersionQueryPublic } from "../graphql/public-generated-rq"

const POLLING_INTERVAL = 5 * 60 * 1000 // 5 minutes

export const VersionChecker = () => {
  const toastRef = useRef<string | null>(null)
  const toast = useToast()
  const [enabled, setEnabled] = useState(true)

  const isAdmin =
    typeof document !== "undefined"
      ? document?.location.pathname.startsWith("/admin")
      : false

  const showVersionToast = useCallback(() => {
    if (toastRef.current) toast.dismiss(toastRef.current)
    toastRef.current = toast.message("New version available! Please refresh the app.", {
      action: {
        label: "Refresh",
        onClick: () => window.location.reload(),
      },
      showClose: false,
      duration: Infinity,
    })
  }, [toast])

  useEffect(() => {
    function callback() {
      setEnabled(document.visibilityState === "visible")
    }

    document.addEventListener("visibilitychange", callback)
    return () => document.removeEventListener("visibilitychange", callback)
  }, [])

  const VersionCheckerComponent = isAdmin ? VersionCheckerAdmin : VersionCheckerPublic
  return (
    <VersionCheckerComponent
      onDifferentVersion={showVersionToast}
      enabled={!isAdmin && enabled && !IS_DEV}
    />
  )
}

const VersionCheckerAdmin = ({
  onDifferentVersion,
  enabled = true,
}: {
  onDifferentVersion: () => void
  enabled: boolean
}) => {
  useGetVersionQueryAdmin(undefined, {
    refetchInterval: POLLING_INTERVAL,
    structuralSharing: (prev, next) => {
      // @ts-expect-error - structuralSharing is not typed correctly https://github.com/TanStack/query/issues/6525
      if (prev && next && prev.schemaVersion.version !== next.schemaVersion.version)
        onDifferentVersion()
      return next
    },
    enabled,
  })
  return null
}

const VersionCheckerPublic = ({
  onDifferentVersion,
  enabled = true,
}: {
  onDifferentVersion: () => void
  enabled: boolean
}) => {
  useGetVersionQueryPublic(undefined, {
    refetchInterval: POLLING_INTERVAL,
    structuralSharing: (prev, next) => {
      // @ts-expect-error - structuralSharing is not typed correctly https://github.com/TanStack/query/issues/6525
      if (prev && next && prev.schemaVersion.version !== next.schemaVersion.version)
        onDifferentVersion()
      return next
    },
    enabled,
  })
  return null
}
